import React, { useState } from 'react';
import Logo from '../Logo';

const NAV_LINKS = [
  { label: 'Terms of Service', href: '/terms' },
  { label: 'Privacy Policy', href: '/privacy' },
];

const TABS = [
  { id: 'ethics', label: 'Ethics & Principles', shortLabel: 'Ethics' },
  { id: 'aup', label: 'Acceptable Use', shortLabel: 'Use Policy' },
  { id: 'dpa', label: 'Data Processing Addendum', shortLabel: 'DPA' },
];

const TrustAndSafety = () => {
  const [activeTab, setActiveTab] = useState('ethics');

  return (
    <div className="min-h-screen bg-[#f9f9fb]">
      {/* Nav */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/">
            <Logo width="208" height="32" textFill="black" />
          </a>
          <div className="flex items-center gap-6">
            {NAV_LINKS.map(l => (
              <a key={l.href} href={l.href} className="hidden md:inline text-gray-600 text-sm font-medium hover:text-[#055EFD] transition-colors">{l.label}</a>
            ))}
            <a href="/" className="text-gray-600 text-sm font-medium hover:text-[#055EFD] transition-colors">Home</a>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 text-center font-['Montserrat']">Trust & Safety</h1>
          <p className="text-center text-gray-500 text-sm mb-8">
            Our commitment to responsible AI, platform security, and developer trust.
          </p>

          {/* Tabs */}
          <div className="bg-gray-100 rounded-xl p-1.5 flex gap-1.5 mb-10">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-2.5 px-2 rounded-lg text-xs md:text-sm font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#055EFD] text-white shadow-md'
                    : 'text-gray-600 hover:bg-gray-200'
                }`}
              >
                <span className="hidden md:inline">{tab.label}</span>
                <span className="md:hidden">{tab.shortLabel}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          {activeTab === 'ethics' && <EthicsTab />}
          {activeTab === 'aup' && <AcceptableUseTab />}
          {activeTab === 'dpa' && <DPATab />}

          {/* Enterprise CTA */}
          <div className="mt-12 p-8 bg-gray-50 border border-dashed border-gray-300 rounded-2xl text-center">
            <h3 className="font-bold text-gray-800 mb-2 font-['Montserrat']">Need a custom agreement?</h3>
            <p className="text-gray-500 text-sm mb-4">
              Enterprise customers with specific compliance, data residency, or SLA requirements can request custom legal agreements.
            </p>
            <a href="mailto:legal@remote-assistant.io" className="text-[#055EFD] font-bold hover:underline text-sm">
              Contact Legal Team &rarr;
            </a>
          </div>
        </div>
      </main>

      <footer className="py-8 text-center text-gray-400 text-xs border-t border-gray-200">
        &copy; 2026 Remote Assistant. Compliant with EU AI Act, GDPR, and CCPA standards.
      </footer>
    </div>
  );
};

/* ─── Ethics & Principles ─── */
const EthicsTab = () => (
  <div className="space-y-8">
    <div className="bg-blue-50 border-l-4 border-[#055EFD] p-6 rounded-r-xl">
      <h2 className="text-lg font-bold text-blue-900 mb-2 font-['Montserrat']">Our AI Ethics Principles</h2>
      <p className="text-blue-800 text-sm leading-relaxed">
        We build AI infrastructure that developers can trust. Our ethics principles are non-negotiable and apply to every interaction processed through our Platform.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {[
        {
          icon: 'fa-eye',
          title: 'Transparency by Default',
          desc: 'AI agents deployed on our Platform must identify themselves as automated systems. We strictly prohibit "ghosting" — impersonating a human being without disclosure. Our TTS voices and conversational patterns are designed to be clearly AI-identifiable.',
        },
        {
          icon: 'fa-lock',
          title: 'Data Sovereignty',
          desc: 'Your data is yours. We do not use developer configurations, end-user conversations, or voice data to train global AI models. Each developer\'s data is logically isolated at the infrastructure level (Redis namespaces, DynamoDB partitions).',
        },
        {
          icon: 'fa-balance-scale',
          title: 'Bias Mitigation',
          desc: 'We actively test our STT and TTS pipelines across accents, dialects, and demographics. We monitor LLM outputs for discriminatory patterns and work with model providers to address identified biases.',
        },
        {
          icon: 'fa-user',
          title: 'Human Override',
          desc: 'Every AI interaction includes an escape path to a human operator. Our Platform provides live call monitoring, whisper guidance, and one-click call transfer so staff can intervene at any point during a conversation.',
        },
        {
          icon: 'fa-shield',
          title: 'Security First',
          desc: 'All data encrypted at rest (AES-256) and in transit (TLS 1.2+). API authentication with per-key rate limiting. Infrastructure runs on isolated AWS VPCs with network segmentation and real-time monitoring.',
        },
        {
          icon: 'fa-gavel',
          title: 'Regulatory Compliance',
          desc: 'The Platform is designed to facilitate compliance with GDPR, CCPA, EU AI Act, TCPA, and CAN-SPAM. We provide the tools and documentation developers need to meet their regulatory obligations.',
        },
      ].map((item, i) => (
        <div key={i} className="p-5 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
              <i className={`fa ${item.icon} text-[#055EFD] text-sm`} />
            </div>
            <h3 className="font-bold text-gray-900 text-sm">{item.title}</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed">{item.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Acceptable Use ─── */
const AcceptableUseTab = () => (
  <div className="space-y-8 text-sm text-gray-700 leading-relaxed">
    <p>
      This Acceptable Use Policy ("AUP") governs your use of the Remote Assistant Developer Platform. Violations may result in immediate suspension or termination of your account.
    </p>

    <div>
      <h3 className="font-bold text-gray-900 mb-3 text-base font-['Montserrat']">Prohibited Uses</h3>
      <div className="space-y-4">
        <ProhibitedItem
          title="Illegal Robocalling & Spam"
          desc="You must not use the Platform to make unsolicited robocalls, automated telemarketing calls, or send spam SMS messages in violation of the TCPA, CAN-SPAM Act, or equivalent legislation. All outbound communications must have proper consent."
        />
        <ProhibitedItem
          title="Fraud & Impersonation"
          desc="You must not deploy AI agents that impersonate real individuals, government agencies, law enforcement, emergency services, financial institutions, or healthcare providers for deceptive purposes."
        />
        <ProhibitedItem
          title="Harassment & Abuse"
          desc="You must not use the Platform to harass, threaten, stalk, or intimidate any person. This includes deploying agents that use aggressive, abusive, or discriminatory language."
        />
        <ProhibitedItem
          title="Regulated Data Without Agreement"
          desc="You must not process protected health information (PHI/HIPAA), payment card data (PCI), or other specially regulated data categories through the Platform without a separate written agreement (e.g., BAA)."
        />
        <ProhibitedItem
          title="Platform Abuse"
          desc="You must not circumvent rate limits, abuse free tier allocations, use automated tools to scrape the Platform, or attempt to access other developers' data or resources."
        />
        <ProhibitedItem
          title="Harmful Content"
          desc="You must not configure AI agents to generate, distribute, or facilitate content that promotes violence, self-harm, child exploitation, terrorism, or illegal activities."
        />
      </div>
    </div>

    <div>
      <h3 className="font-bold text-gray-900 mb-3 text-base font-['Montserrat']">Developer Obligations</h3>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong>AI Disclosure:</strong> You must ensure end users are informed they are interacting with an AI system. Failure to disclose may violate the EU AI Act and other emerging regulations.</li>
        <li><strong>Consent Management:</strong> You are responsible for obtaining and managing consent for call recording, data collection, and automated communications as required by law.</li>
        <li><strong>Monitoring:</strong> You should regularly review conversation logs and AI outputs to ensure your agents operate within acceptable parameters.</li>
        <li><strong>Incident Reporting:</strong> If you discover that your agent has been used for prohibited purposes (e.g., by a compromised API key), notify us immediately at <a href="mailto:security@remote-assistant.io" className="text-[#055EFD] hover:underline">security@remote-assistant.io</a>.</li>
        <li><strong>Regulatory Compliance:</strong> You are responsible for compliance with all local, national, and international laws applicable to your use of the Platform, including telecommunications, privacy, and consumer protection regulations.</li>
      </ul>
    </div>

    <div>
      <h3 className="font-bold text-gray-900 mb-3 text-base font-['Montserrat']">Enforcement</h3>
      <p>
        We monitor Platform usage for policy violations. When a violation is detected:
      </p>
      <ol className="list-decimal pl-6 space-y-2 mt-2">
        <li><strong>Warning:</strong> For first-time, minor violations, we may issue a warning with a required corrective action.</li>
        <li><strong>Suspension:</strong> For repeated or serious violations, API access and telephony services may be suspended pending review.</li>
        <li><strong>Termination:</strong> For severe violations (fraud, illegal activity, safety threats), accounts may be terminated immediately without prior notice.</li>
        <li><strong>Reporting:</strong> We reserve the right to report illegal activity to law enforcement authorities.</li>
      </ol>
    </div>

    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <p className="text-amber-800 text-xs">
        <strong>Telephony-Specific Note:</strong> Phone numbers provisioned through the Platform are subject to carrier policies. Abuse of telephony services (spam, spoofing, excessive call volumes to emergency numbers) may result in number deprovisioning by upstream carriers, which is outside our control.
      </p>
    </div>
  </div>
);

const ProhibitedItem = ({ title, desc }) => (
  <div className="flex gap-3 items-start">
    <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
      <i className="fa fa-times text-red-500 text-[10px]" />
    </div>
    <div>
      <h4 className="font-semibold text-gray-900 text-sm mb-1">{title}</h4>
      <p className="text-xs text-gray-600 leading-relaxed">{desc}</p>
    </div>
  </div>
);

/* ─── Data Processing Addendum ─── */
const DPATab = () => (
  <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
    <div>
      <h2 className="text-base font-bold text-gray-900 border-b border-gray-200 pb-3 mb-4 font-['Montserrat']">Data Processing Addendum (DPA)</h2>
      <p className="text-xs text-gray-500 italic">Last Updated: April 2026</p>
    </div>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">1. Parties & Relationship</h3>
      <p>
        This DPA applies to the processing of Personal Data by Remote Assistant (the "Processor") on behalf of the Developer (the "Controller") in connection with the Developer's use of the Remote Assistant Developer Platform. This addendum supplements and forms part of the <a href="/terms" className="text-[#055EFD] hover:underline">Terms of Service</a>.
      </p>
    </section>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">2. Scope of Processing</h3>
      <table className="w-full text-xs border border-gray-200 rounded-lg overflow-hidden mt-2">
        <tbody className="divide-y divide-gray-100">
          <tr><td className="p-3 font-medium bg-gray-50 w-1/3">Data Subjects</td><td className="p-3">End users who interact with Developer's AI agents (callers, SMS recipients, email correspondents)</td></tr>
          <tr><td className="p-3 font-medium bg-gray-50">Categories of Data</td><td className="p-3">Phone numbers, names, email addresses, voice transcriptions, conversation content, booking/scheduling data, interaction metadata</td></tr>
          <tr><td className="p-3 font-medium bg-gray-50">Processing Activities</td><td className="p-3">Speech-to-text transcription, LLM inference, text-to-speech synthesis, conversation storage, business logic execution, SMS/email delivery</td></tr>
          <tr><td className="p-3 font-medium bg-gray-50">Duration</td><td className="p-3">For the term of the Developer's account plus 30-day post-termination retention period</td></tr>
        </tbody>
      </table>
    </section>

    <section className="bg-blue-50 p-5 rounded-lg border border-blue-200">
      <h3 className="font-bold text-gray-900 mb-2">3. The No-Global-Training Guarantee</h3>
      <p className="text-blue-800 text-xs leading-relaxed">
        Processor warrants that Controller's Personal Data and conversational inputs shall <strong>not</strong> be utilized to train, retrain, fine-tune, or improve foundational large language models (LLMs), speech models, or any other AI models used across the Processor's broader customer base. Data is logically isolated to the Controller's account using separate Redis key namespaces and DynamoDB partition keys.
      </p>
    </section>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">4. Technical & Organizational Measures</h3>
      <ul className="list-disc pl-5 space-y-2">
        <li><strong>Encryption:</strong> AES-256 at rest (AWS KMS), TLS 1.2+ in transit for all API and data plane traffic</li>
        <li><strong>Access Control:</strong> Role-based access control (RBAC) with MFA for all internal systems. Developer data access requires explicit justification and is audit-logged.</li>
        <li><strong>Data Isolation:</strong> Each developer's data is isolated via Redis key namespaces (<code className="text-xs bg-gray-100 px-1 rounded">context_history:&#123;phone&#125;:&#123;session&#125;</code>) and DynamoDB partition keys. No cross-tenant data access is possible at the application layer.</li>
        <li><strong>Data Minimization:</strong> Voice audio is processed in real-time streams and not persistently stored. PII in conversation logs can be configured for auto-redaction.</li>
        <li><strong>Incident Response:</strong> Security incidents are assessed within 4 hours and affected developers notified within 72 hours as required by GDPR Article 33.</li>
        <li><strong>Backups:</strong> Database backups are encrypted and retained for 30 days. Backup data follows the same access controls as production data.</li>
      </ul>
    </section>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">5. Sub-Processors</h3>
      <p className="mb-3">
        Processor uses the following sub-processors for AI computation and service delivery. Controller acknowledges and consents to the use of these sub-processors:
      </p>
      <div className="overflow-x-auto">
        <table className="min-w-full text-xs border border-gray-200 rounded-lg overflow-hidden">
          <thead>
            <tr className="bg-gray-50">
              <th className="p-3 border-b border-gray-200 text-left font-semibold">Entity</th>
              <th className="p-3 border-b border-gray-200 text-left font-semibold">Role</th>
              <th className="p-3 border-b border-gray-200 text-left font-semibold">Data Processed</th>
              <th className="p-3 border-b border-gray-200 text-left font-semibold">Location</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr><td className="p-3 font-medium">AWS</td><td className="p-3">Infrastructure</td><td className="p-3">All platform data</td><td className="p-3">US (us-east-1)</td></tr>
            <tr><td className="p-3 font-medium">OpenAI</td><td className="p-3">LLM + TTS</td><td className="p-3">Conversation text</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Groq</td><td className="p-3">LLM (low-latency)</td><td className="p-3">Conversation text</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Deepgram</td><td className="p-3">STT</td><td className="p-3">Voice audio streams</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Cartesia</td><td className="p-3">TTS</td><td className="p-3">Response text</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Google Cloud</td><td className="p-3">TTS</td><td className="p-3">Response text</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Twilio</td><td className="p-3">Telephony + SMS</td><td className="p-3">Phone numbers, call audio, SMS content</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Daily.co</td><td className="p-3">WebRTC transport</td><td className="p-3">Audio streams</td><td className="p-3">US</td></tr>
            <tr><td className="p-3 font-medium">Stripe</td><td className="p-3">Payments</td><td className="p-3">Billing information</td><td className="p-3">Global</td></tr>
            <tr><td className="p-3 font-medium">Redis Cloud</td><td className="p-3">Session state</td><td className="p-3">Conversation context, config cache</td><td className="p-3">US</td></tr>
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-gray-500">
        Processor will notify Controller of any intended changes to sub-processors at least 30 days in advance. Controller may object to a new sub-processor within 14 days; if the objection cannot be resolved, Controller may terminate the Services.
      </p>
    </section>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">6. Data Subject Rights</h3>
      <p>
        Processor shall assist Controller in fulfilling data subject requests (access, rectification, erasure, portability, restriction, objection) by providing:
      </p>
      <ul className="list-disc pl-5 space-y-1 mt-2">
        <li>API endpoints for programmatic data export and deletion</li>
        <li>Dashboard tools for conversation log management</li>
        <li>Manual support within 10 business days for complex requests via <a href="mailto:dpo@remote-assistant.io" className="text-[#055EFD] hover:underline">dpo@remote-assistant.io</a></li>
      </ul>
    </section>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">7. Compliance Framework</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
        {[
          { reg: 'GDPR', desc: 'Lawful basis documentation, DPA, data subject rights, 72-hour breach notification, SCCs for international transfers' },
          { reg: 'CCPA/CPRA', desc: 'Do-not-sell commitment, consumer rights support, service provider obligations, data minimization' },
          { reg: 'EU AI Act', desc: 'Transparency requirements, deployer obligations support, human oversight tooling, risk assessment documentation' },
        ].map((item, i) => (
          <div key={i} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h4 className="font-bold text-gray-900 text-xs mb-1">{item.reg}</h4>
            <p className="text-[11px] text-gray-600 leading-relaxed">{item.desc}</p>
          </div>
        ))}
      </div>
    </section>

    <section>
      <h3 className="font-bold text-gray-900 mb-2">8. Audit Rights</h3>
      <p>
        Controller may audit Processor's compliance with this DPA by requesting our most recent SOC 2 report or equivalent third-party audit documentation. On-site audits may be conducted once per year with 30 days' written notice, at Controller's expense, subject to reasonable confidentiality protections.
      </p>
    </section>
  </div>
);

export default TrustAndSafety;
